// Notification Sound Utility
// Uses Web Audio API to generate notification sounds — no external mp3 file needed.

class NotificationSound {
    constructor() {
        this.isEnabled = true;
        this.volume = 0.7;
        this.lastPlayTime = 0;
        this.cooldownPeriod = 300; // 300ms cooldown — allow rapid but not spammy
        this.audioContext = null;
    }

    /**
     * Lazily create or resume the AudioContext.
     * Must be called inside a user-gesture on iOS/Safari the first time.
     */
    getAudioContext() {
        if (!this.audioContext) {
            const AC = window.AudioContext || window.webkitAudioContext;
            if (!AC) return null;
            this.audioContext = new AC();
        }
        // Resume suspended context (required after user gesture on some browsers)
        if (this.audioContext.state === 'suspended') {
            this.audioContext.resume().catch(() => {});
        }
        return this.audioContext;
    }

    /**
     * Play a pleasant two-tone notification chime using Web Audio API.
     * Works on all modern browsers without needing an mp3 file.
     */
    async play() {
        if (!this.isEnabled) return;

        // Cooldown check
        const now = Date.now();
        if (now - this.lastPlayTime < this.cooldownPeriod) return;
        this.lastPlayTime = now;

        try {
            const ctx = this.getAudioContext();
            if (!ctx) return;

            const t = ctx.currentTime;

            // ── First tone (higher) ──
            const osc1 = ctx.createOscillator();
            const gain1 = ctx.createGain();
            osc1.type = 'sine';
            osc1.frequency.setValueAtTime(830, t);        // A5-ish
            gain1.gain.setValueAtTime(this.volume * 0.4, t);
            gain1.gain.exponentialRampToValueAtTime(0.001, t + 0.15);
            osc1.connect(gain1);
            gain1.connect(ctx.destination);
            osc1.start(t);
            osc1.stop(t + 0.15);

            // ── Second tone (slightly higher — creates a pleasant "ding-ding") ──
            const osc2 = ctx.createOscillator();
            const gain2 = ctx.createGain();
            osc2.type = 'sine';
            osc2.frequency.setValueAtTime(1050, t + 0.12); // C6-ish
            gain2.gain.setValueAtTime(0.001, t);
            gain2.gain.setValueAtTime(this.volume * 0.35, t + 0.12);
            gain2.gain.exponentialRampToValueAtTime(0.001, t + 0.35);
            osc2.connect(gain2);
            gain2.connect(ctx.destination);
            osc2.start(t + 0.12);
            osc2.stop(t + 0.35);

        } catch (error) {
            console.warn('Failed to play notification sound:', error);
        }
    }

    setVolume(volume) {
        this.volume = Math.max(0, Math.min(1, volume));
        localStorage.setItem('notificationVolume', this.volume.toString());
    }

    enable() {
        this.isEnabled = true;
        localStorage.setItem('notificationEnabled', 'true');
    }

    disable() {
        this.isEnabled = false;
        localStorage.setItem('notificationEnabled', 'false');
    }

    isEnabledCheck() {
        const saved = localStorage.getItem('notificationEnabled');
        return saved !== 'false';
    }
}

// Create singleton instance
const notificationSound = new NotificationSound();

// Load saved preferences
if (typeof window !== 'undefined') {
    try {
        const savedEnabled = localStorage.getItem('notificationEnabled');
        if (savedEnabled === 'false') notificationSound.isEnabled = false;
        const savedVolume = localStorage.getItem('notificationVolume');
        if (savedVolume) notificationSound.volume = parseFloat(savedVolume);
    } catch {} // eslint-disable-line no-empty

    // Unlock AudioContext on first user interaction (required by iOS Safari & Chrome)
    const unlockAudio = () => {
        notificationSound.getAudioContext();
        ['touchstart', 'touchend', 'mousedown', 'keydown'].forEach(evt =>
            document.removeEventListener(evt, unlockAudio, true)
        );
    };
    ['touchstart', 'touchend', 'mousedown', 'keydown'].forEach(evt =>
        document.addEventListener(evt, unlockAudio, true)
    );
}

export default notificationSound;